// BIZ-PLAYBOOKS and BIZ-CLIENT-HEALTH (docs/direction/2026-10-30-true-business-gaps.md, gaps 1 and
// 2). Base plan (`feature: core`). Handlers: services/api/src/routes/business.ts.
//
// Playbooks are SOP pages (Docs): these routes read and set what makes one a playbook — its owner
// seat, the seats that follow it (people or DEs), the item templates a run starts from — and what
// it shows: who follows it, when it last changed and by whom, its runs' checklist completion and
// the DE proposals waiting. A DE proposes a new version as a `doc_update` approval (POST approvals).
//
// Client health: a `client` space's score from the signals CompanyOS has (late work, reopened
// work, open client issues, first-reply time, NPS/CSAT answers), its trend from the daily
// snapshots, and the surveys sent about it. A survey's respondent answers behind its link, with no
// account (`survey.*`, the token in the path, like an assessment respondent).
import { z } from "zod";
import { ClientHealthBand, ClientSurveyKind, RecallPushState } from "../enums";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { ProcedureRunView } from "./memory";
import { PageReader } from "./pages";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

// Playbooks ---------------------------------------------------------------------------------------

/** A seat as a playbook shows it: its title and who holds it now (DE or person). */
export const PlaybookSeat = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    holders: z.array(
      z.object({
        membership_id: Uuid,
        display_name: z.string(),
        kind: z.enum(["person", "digital_employee"]),
      }),
    ),
  })
  .meta({ id: "PlaybookSeat" });

export const PlaybookTemplate = z
  .object({ id: Uuid, name: z.string(), board_id: Uuid, board_name: z.string() })
  .meta({ id: "PlaybookTemplate" });

/** Runs of the playbook (tasks made from its templates) over the last 90 days. */
export const PlaybookRuns = z
  .object({
    started: z.int(),
    done: z.int(),
    /** Done runs whose every checklist item was ticked. */
    followed: z.int(),
    /** `followed / done` in percent; null with no done run. */
    followed_pct: z.int().nullable(),
  })
  .meta({ id: "PlaybookRuns" });

export const Playbook = z
  .object({
    page_id: Uuid,
    title: z.string(),
    kind: z.string(),
    version: z.int(),
    owner: PlaybookSeat.nullable(),
    followers: z.array(PlaybookSeat),
    /** The agents that read it as knowledge (DOCS-KB sources), beside the following seats. */
    readers: z.array(PageReader),
    templates: z.array(PlaybookTemplate),
    updated_at: Timestamp,
    updated_by: z
      .object({ display_name: z.string(), kind: z.string() })
      .nullable()
      .describe("Who wrote the last version (null: unknown)"),
    runs: PlaybookRuns,
    /** `doc_update` approvals on this page still waiting for a decision. */
    pending_updates: z.int(),
    /** The caller may change the links (edits the page). */
    can_edit: z.boolean(),
    owner_approval: z
      .boolean()
      .describe(
        "Changes need owner approval: a DE's direct edit is refused; it proposes the change (propose_doc_update) for the owner (or its chain) to approve",
      ),
  })
  .meta({ id: "Playbook" });
export type Playbook = z.infer<typeof Playbook>;

export const PlaybookLinksPut = z
  .strictObject({
    owner_seat_id: Uuid.nullable(),
    follower_seat_ids: z.array(Uuid).max(50),
    item_template_ids: z.array(Uuid).max(20),
    /** Absent: unchanged. */
    owner_approval: z.boolean().optional(),
  })
  .meta({ id: "PlaybookLinksPut" });

export const PlaybookSummary = z
  .object({
    page_id: Uuid,
    title: z.string(),
    space_id: Uuid,
    owner: PlaybookSeat.nullable(),
    follower_count: z.int(),
    updated_at: Timestamp,
    followed_pct: z.int().nullable(),
    pending_updates: z.int(),
    owner_approval: z.boolean(),
  })
  .meta({ id: "PlaybookSummary" });
export type PlaybookSummary = z.infer<typeof PlaybookSummary>;

/**
 * RECALL-PROCEDURES: the playbook as a procedure — the Recall Procedure record CompanyOS keeps of
 * it (its steps read from the page) and the runs DEs recorded of it (`procedure_run`, failures
 * too), newest first. One list: the playbook is the procedure.
 */
export const PlaybookProcedure = z
  .object({
    page_id: Uuid,
    procedure: z
      .object({
        title: z.string(),
        step_count: z.int(),
        page_version: z.int(),
        recall_state: RecallPushState,
        recall_detail: z.string().nullable(),
        synced_at: Timestamp.nullable(),
      })
      .nullable(),
    runs: z.object({
      total: z.int(),
      succeeded: z.int(),
      failed: z.int(),
      abandoned: z.int(),
      last_at: Timestamp.nullable(),
    }),
    recent: z.array(ProcedureRunView),
  })
  .meta({ id: "PlaybookProcedure" });
export type PlaybookProcedure = z.infer<typeof PlaybookProcedure>;

export const PlaybookListQuery = z.object({
  seat_id: Uuid.optional().describe("Only the playbooks this seat owns or follows"),
});

// Client health -----------------------------------------------------------------------------------

/** One signal: its 0–100 score (null: no data in the window) and the facts behind it. */
export const ClientHealthSignal = z
  .object({
    key: z.enum(["delays", "rework", "issues", "response", "sentiment"]),
    label: z.string(),
    score: z.int().nullable(),
    weight: z.int(),
    /** One plain line: "2 of 9 dated tasks late", "median first reply 3h". */
    detail: z.string(),
  })
  .meta({ id: "ClientHealthSignal" });

export const ClientHealth = z
  .object({
    space_id: Uuid,
    name: z.string(),
    account_seat: z.object({ seat_id: Uuid, title: z.string() }).nullable(),
    score: z.int().nullable(),
    band: ClientHealthBand,
    /** Score now minus the score 7 days ago (null without both). */
    trend: z.int().nullable(),
    signals: z.array(ClientHealthSignal),
    /** NPS over the answered NPS surveys of the last 90 days (−100…100; null: none). */
    nps: z.int().nullable(),
    computed_at: Timestamp,
  })
  .meta({ id: "ClientHealth" });
export type ClientHealth = z.infer<typeof ClientHealth>;

export const ClientSurveyView = z
  .object({
    id: Uuid,
    space_id: Uuid,
    kind: ClientSurveyKind,
    recipient_name: z.string(),
    recipient_email: z.string().nullable(),
    sent_at: Timestamp,
    mailed_at: Timestamp.nullable(),
    expires_at: Timestamp,
    score: z.int().nullable(),
    comment: z.string().nullable(),
    answered_at: Timestamp.nullable(),
    issue_id: Uuid.nullable(),
  })
  .meta({ id: "ClientSurveyView" });

export const ClientHealthDetail = ClientHealth.extend({
  surveys: z.array(ClientSurveyView),
  history: z.array(
    z.object({ day: z.string(), score: z.int().nullable(), band: ClientHealthBand }),
  ),
  open_issues: z.array(
    z.object({ id: Uuid, title: z.string(), status: z.string(), created_at: Timestamp }),
  ),
}).meta({ id: "ClientHealthDetail" });

export const ClientSurveyCreate = z
  .strictObject({
    kind: ClientSurveyKind,
    recipient_name: text(200),
    recipient_email: z.email().max(320).nullable().optional(),
  })
  .meta({ id: "ClientSurveyCreate" });

export const ClientSurveyCreated = z
  .object({
    survey: ClientSurveyView,
    /** The respondent's link (path; shown once). Mailed too when an email was given. */
    link: z.string(),
  })
  .meta({ id: "ClientSurveyCreated" });

export const SurveyTokenParams = z.object({
  co: Uuid,
  token: z.string().min(20).max(200),
});

export const SurveyRespondentView = z
  .object({
    company_name: z.string(),
    client_name: z.string(),
    kind: ClientSurveyKind,
    recipient_name: z.string(),
    answered: z.boolean(),
    expired: z.boolean(),
  })
  .meta({ id: "SurveyRespondentView" });

export const SurveyAnswer = z
  .strictObject({
    score: z.int().min(0).max(10),
    comment: z.string().max(2000).optional(),
  })
  .meta({ id: "SurveyAnswer" });

const survey = "/v1/companies/{co}/survey/{token}";

export const businessRoutes = {
  "pages.playbook": defineRoute({
    ...core,
    id: "pages.playbook",
    method: "get",
    path: `${base}/pages/{id}/playbook`,
    tag: "pages",
    summary:
      "A doc as a playbook: owner seat, the seats and DEs that follow it, its run templates, last update and its runs' checklist completion",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Playbook },
    errors: [],
    emits: [],
  }),
  "pages.playbookSet": defineRoute({
    ...core,
    id: "pages.playbookSet",
    method: "put",
    path: `${base}/pages/{id}/playbook`,
    tag: "pages",
    summary:
      "Set a playbook's owner seat, follower seats and run templates (the whole set; the page's editors)",
    access: "member",
    params: CompanyItemParams,
    body: PlaybookLinksPut,
    response: { status: 200, schema: Playbook },
    errors: [],
    emits: ["playbook.links_changed"],
  }),
  "pages.playbookProcedure": defineRoute({
    ...core,
    id: "pages.playbookProcedure",
    method: "get",
    path: `${base}/pages/{id}/playbook/procedure`,
    tag: "pages",
    summary:
      "A playbook as a procedure: its Recall Procedure record and the runs DEs recorded of it (failures too)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PlaybookProcedure },
    errors: [],
    emits: [],
  }),
  "playbooks.list": defineRoute({
    ...core,
    id: "playbooks.list",
    method: "get",
    path: `${base}/playbooks`,
    tag: "pages",
    summary: "The company's playbooks (SOP docs) the caller can view, or one seat's",
    access: "member",
    params: CompanyParams,
    query: PlaybookListQuery,
    response: { status: 200, schema: z.object({ data: z.array(PlaybookSummary) }) },
    errors: [],
    emits: [],
  }),
  "clients.health": defineRoute({
    ...core,
    id: "clients.health",
    method: "get",
    path: `${base}/clients/health`,
    tag: "spaces",
    summary: "Every client space the caller can view, with its health score, band and trend",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(ClientHealth) }) },
    errors: [],
    emits: [],
  }),
  "clients.healthGet": defineRoute({
    ...core,
    id: "clients.healthGet",
    method: "get",
    path: `${base}/clients/{id}/health`,
    tag: "spaces",
    summary: "One client's health: the signals, 30 days of history, its surveys and open issues",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ClientHealthDetail },
    errors: [],
    emits: [],
  }),
  "clients.surveyCreate": defineRoute({
    ...core,
    id: "clients.surveyCreate",
    method: "post",
    path: `${base}/clients/{id}/surveys`,
    tag: "spaces",
    summary:
      "Send an NPS or CSAT survey about a client (mailed when an email is given). A DE's send is held to its policy (`external`): an Ask answers 409",
    access: "member",
    params: CompanyItemParams,
    body: ClientSurveyCreate,
    idempotency: "optional",
    response: { status: 201, schema: ClientSurveyCreated },
    errors: ["policy_denied", "conflict"],
    emits: ["client_survey.sent"],
  }),
  "survey.get": defineRoute({
    ...core,
    id: "survey.get",
    method: "get",
    path: survey,
    tag: "spaces",
    summary: "A client survey's question, by its link",
    access: "assessment_token",
    params: SurveyTokenParams,
    response: { status: 200, schema: SurveyRespondentView },
    errors: [],
    emits: [],
  }),
  "survey.answer": defineRoute({
    ...core,
    id: "survey.answer",
    method: "post",
    path: survey,
    tag: "spaces",
    summary: "Answer a client survey (once); a detractor's answer raises a client issue",
    access: "assessment_token",
    params: SurveyTokenParams,
    body: SurveyAnswer,
    response: { status: 200, schema: SurveyRespondentView },
    errors: ["conflict"],
    emits: ["client_survey.answered", "issue.raised"],
  }),
};
